if (!customElements.get("quick-view-modal")) {
  customElements.define(
    "quick-view-modal",
    class QuickViewModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickViewInfo-"]');
        if (!this.modalContent) return;

        this.preventScrollBody();
      }

      hide(preventFocus = false) {
        this.modalContent.innerHTML = "";

        if (preventFocus) this.openedBy = null;

        super.hide();

        document.body.dispatchEvent(new CustomEvent("quick-view-closed"));
      }

      show(opener) {
        if (!opener || !opener?.dataset?.productUrl) return;

        opener.setAttribute("aria-disabled", true);
        opener.classList.add("loading");
        const btnSpinner = opener.querySelector(".loading-overlay__spinner");
        if (btnSpinner) btnSpinner.classList.remove("hidden");

        fetch(opener.dataset.productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.productElement = responseHTML.querySelector(
              '[id^="MainProduct-"]'
            );
            if (!this.productElement) return;

            this.prepareProductHTML();
            this.setModalHTML(opener);
            this.initSlider();
            this.initShopifyElements();
            super.show(opener);
            this.setFocusElement(opener);
            this.forceReflow();
          })
          .finally(() => {
            opener.removeAttribute("aria-disabled");
            opener.classList.remove("loading");
            if (btnSpinner) btnSpinner.classList.add("hidden");
          });
      }

      prepareProductHTML() {
        this.preventDuplicatedIDs();
        this.removeDOMElements(this.productElement);
      }

      removeDOMElements(mainProductElement) {
        const selectors = [
          ".product-popup",
          ".product__share",
          ".product__breadcrumbs",
          ".product-sticky-add-bar",
          "product-modal",
          ".pickup-availability",
          ".back-in-stock-alert",
          "back-in-stock-alert-drawer",
          ".complementary-products",
          ".product-collapsible-info",
          ".product__media-toggle",
          ".product__media-mobile-icon",
          ".product__media-cursor",
          ".product__media-info",
          "[data-back-in-stock-alert-script]",
          ".custom-liquid",
          ".product-accordion",
          ".product-app",
          ".product-countdown",
          ".product__slider-nav",
          ".product__media-arrows",
        ];
        const removingElements = mainProductElement.querySelectorAll(
          selectors.join(", ")
        );
        removingElements.forEach((el) => el.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickview-${sectionId}`
          );
        this.productElement
          .querySelectorAll("variant-picker")
          .forEach((variantPicker) => {
            variantPicker.dataset.originalSection = sectionId;
          });
      }

      setModalHTML(opener) {
        this.modalContent.innerHTML = this.productElement.innerHTML;

        this.reinjectScriptTags();
        this.createFullDetailsButton(opener);
        this.preventVariantURLSwitching();
        this.clearMediaClasses();
      }

      reinjectScriptTags() {
        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        this.modalContent.querySelectorAll("script").forEach((oldScriptTag) => {
          const newScriptTag = document.createElement("script");
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML)
          );
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        const variantPickerEl =
          this.modalContent.querySelector("variant-picker");
        if (variantPickerEl) {
          variantPickerEl.setAttribute("data-update-url", "false");
        }
      }

      preventScrollBody() {
        // iOS Safari scrolls the page behind the fixed modal when a clipped
        // option radio receives focus (body overflow lock is ignored there).
        // Delegated focusin survives variant-change re-renders of the options;
        // called once from the constructor.
        this.modalContent.addEventListener("focusin", (event) => {
          if (
            !event.target.matches(
              '.product-form__controls-group input[type="radio"]'
            )
          ) {
            return;
          }
          const scrollTop =
            document.body.scrollTop || document.documentElement.scrollTop;
          setTimeout(() => {
            window.scrollTo(0, scrollTop);
          }, 0);
        });
      }

      initShopifyElements() {
        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
      }

      initSlider() {
        const sliderEl = this.modalContent.querySelector(
          ".product__media-list[data-quick-view-type]"
        );
        if (!sliderEl) return;

        const sliderWrapper = sliderEl.querySelector(
          ".product__media-list-container"
        );
        const slides = sliderEl.querySelectorAll(".product__media-item");
        const quickViewType = sliderEl.dataset.quickViewType || "slider";

        sliderWrapper.classList.add("swiper-wrapper");
        slides.forEach((slide) => slide.classList.add("swiper-slide"));

        const slideCount = slides.length;
        const speed = slideCount > 30 ? 0 : 800;

        const mediaWrapper = this.modalContent.querySelector(".product__media");

        // Clone nav from template — keeps the template in DOM across reopens
        const navTemplate = this.querySelector(
          ".quick-view-modal__nav-template"
        );
        const navEl = navTemplate
          ? navTemplate.content.firstElementChild.cloneNode(true)
          : null;
        if (navEl && mediaWrapper) {
          mediaWrapper.appendChild(navEl);
        }

        const buttonPrev = navEl?.querySelector(".swiper-button-prev");
        const buttonNext = navEl?.querySelector(".swiper-button-next");
        const slideCountEl = navEl?.querySelector(
          ".quick-view-modal__nav-count"
        );

        const swiperOptions = {
          slidesPerView: 1,
          spaceBetween: 8,
          speed: speed,
          loop: false,
          watchSlideProgress: true,
          allowTouchMove: true,
          mousewheel: {
            forceToAxis: true,
          },
          navigation: {
            nextEl: buttonNext,
            prevEl: buttonPrev,
            disabledClass: "swiper-button-disabled",
          },
          on: {
            init: function () {
              if (slideCountEl) {
                slideCountEl.textContent = `1/${this.slides.length}`;
              }
            },
            slideChange: function () {
              if (slideCountEl) {
                slideCountEl.textContent = `${this.activeIndex + 1}/${
                  this.slides.length
                }`;
              }
            },
          },
        };

        // For stacked type: disable slider interaction on desktop (CSS handles the layout)
        if (quickViewType === "stacked") {
          swiperOptions.breakpoints = {
            990: {
              enabled: false,
              allowTouchMove: false,
            },
          };
        }

        new Swiper(sliderEl, swiperOptions);
      }

      createFullDetailsButton(opener) {
        const buttonsWrapper = this.modalContent.querySelector(
          ".product-form__buttons"
        );
        const fullDetailsBtnTemplate = this.querySelector(
          "[data-full-details-button]"
        );
        if (!buttonsWrapper || !fullDetailsBtnTemplate) return;

        const fullDetailsBtn = fullDetailsBtnTemplate.content.cloneNode(true);

        buttonsWrapper.appendChild(fullDetailsBtn);
      }

      clearMediaClasses() {
        const mediaWrapper = this.modalContent.querySelector(".product__media");
        if (!mediaWrapper) return;

        const keepClasses = ["product__media", "product__media--no-media"];
        const filteredClasses = [...mediaWrapper.classList].filter((cssClass) =>
          keepClasses.includes(cssClass)
        );

        mediaWrapper.className = filteredClasses.join(" ");
      }

      setFocusElement(opener) {
        const productCard = opener.closest("product-card");
        const productCardLink = productCard?.querySelector(
          ".product-card__link-overlay"
        );
        if (productCardLink) this.openedBy = productCardLink;
      }

      // Force reflow to prevent Safari iOS fieldset rendering issues.
      forceReflow() {
        requestAnimationFrame(() => {
          this.modalContent
            .querySelectorAll("fieldset.product-form__controls")
            .forEach((fieldset) => {
              fieldset.style.display = "grid";
              void fieldset.offsetHeight;
              fieldset.style.display = "";
            });
        });
      }
    }
  );
}
